import { useQuery, useMutation } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import CampaignIcon from '@mui/icons-material/Campaign';
import styled from 'styled-components';
import { GET_ANNOUNCEMENTS, DISMISS_ANNOUNCEMENT } from 'modules/Apollo/queries';
import { logger } from 'modules/logger';
import { Colors, Sizes } from 'styles';
import type { GetAnnouncementsQuery } from 'generated/graphql';
import { useEffect } from 'react';

const BannerContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background-color: ${Colors.blue1};
  border-bottom: 1px solid ${Colors.blue2};
`;

const StyledCampaignIcon = styled(CampaignIcon)`
  font-size: 20px;
  color: ${Colors.blue4};
  flex-shrink: 0;
`;

const BannerText = styled.span`
  color: ${Colors.gray9};
  flex: 1;
  font-size: ${Sizes.textBody2};
`;

const LearnMoreLink = styled.a`
  color: ${Colors.blue4};
  text-decoration: underline;

  &:hover {
    color: ${Colors.blue5};
  }
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  border-radius: 50%;
  cursor: pointer;
  color: ${Colors.gray7};
  flex-shrink: 0;

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }
`;

const StyledCloseIcon = styled(CloseIcon)`
  font-size: 16px;
`;

export function AnnouncementBanner() {
  const { data, error, loading } = useQuery<GetAnnouncementsQuery>(GET_ANNOUNCEMENTS, {
    context: { telemetry: { component: 'AnnouncementBanner' } },
  });

  const [dismissAnnouncement] = useMutation(DISMISS_ANNOUNCEMENT, {
    context: { telemetry: { component: 'AnnouncementBanner' } },
    refetchQueries: [{ query: GET_ANNOUNCEMENTS }],
    onError: (err) => {
      logger.error('[AnnouncementBanner] Failed to dismiss announcement', err);
    },
  });

  useEffect(() => {
    if (error != null) {
      logger.error('[AnnouncementBanner] Failed to fetch announcements', error);
    }
  }, [error]);

  if (loading || !data?.announcements?.length) {
    return null;
  }

  const announcement = data.announcements[0];

  const handleDismiss = () => {
    dismissAnnouncement({
      variables: { announcementId: announcement.id },
      optimisticResponse: { dismissAnnouncement: true },
    });
  };

  return (
    <BannerContainer>
      <StyledCampaignIcon />
      <BannerText>
        <span>{announcement.title}</span>
        {announcement.url && (
          <>
            {' — '}
            <LearnMoreLink href={announcement.url} target="_blank" rel="noopener noreferrer">
              Learn more
            </LearnMoreLink>
          </>
        )}
      </BannerText>
      <DismissButton type="button" onClick={handleDismiss} aria-label="Dismiss announcement">
        <StyledCloseIcon />
      </DismissButton>
    </BannerContainer>
  );
}
